import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// Fuente única del despliegue con transición de todo el proyecto: filas de tabla que se expanden,
// secciones plegables de formulario, paneles. Antes cada superficie hacía `{open && <div>}`, que no
// es una animación rápida sino la AUSENCIA de animación — el contenido aparecía de golpe.
//
// La duración y la curva viven acá y no en cada llamador. Si estuvieran repartidas, ajustar el ritmo
// significaría encontrar N literales, y el primero que se olvide deja una superficie fuera de tiempo
// sin que falle nada: el tipo de inconsistencia que sólo se descubre usando la app.
export const REVEAL_MS = 420;
// easeOutQuad. Desacelera al final (una entrada que frena se siente natural; una lineal, mecánica)
// pero reparte el recorrido en vez de gastarlo en los primeros milisegundos, que es lo que hace que
// una easeOutExpo se lea como instantánea aunque dure 240ms.
export const REVEAL_EASE = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

// ── Por qué `grid-template-rows` y no `height` ni `max-height` ────────────────
// `height: auto` no es interpolable en ningún elemento, así que la transición de altura clásica
// obliga a inventar un `max-height` mayor que el contenido — y ese número miente el día que el
// contenido crece: o corta lo de abajo, o deja la animación corriendo en vacío durante la parte que
// sobra, con lo que la duración real deja de ser la declarada.
//
// El navegador SÍ interpola entre fracciones de grid. Un contenedor `grid` que va de
// `grid-template-rows: 0fr` a `1fr` mide el contenido real: sin números mágicos y sin recortes
// cuando una ficha tiene tres campos más que otra.
//
// El hijo necesita `min-height: 0` además de `overflow: hidden`. No es decorativo: un item de grid
// tiene `min-height: auto` por defecto y se niega a encogerse por debajo de su contenido, así que
// sin eso la pista de `0fr` no cierra y no se ve ninguna animación.
//
// ── Por qué no alcanza con `{open && …}` ─────────────────────────────────────
// Un elemento que NACE en su estado final no transiciona: el navegador no tiene valor previo desde
// el cual interpolar. Por eso montar y abrir son dos pasos separados, y al cerrar hay que sostener
// el nodo montado hasta que la transición termine.
//
// ⚠️ En estado de reposo el comportamiento es IDÉNTICO al `{open && …}` que reemplaza: cerrado =
// desmontado. No es un detalle estético — en un formulario, un `<input required>` que quedara
// montado dentro de una sección plegada bloquearía el submit sin que se vea por qué.
const useReveal = (open, duration) => {
  const [montado, setMontado] = useState(open);
  const [abierto, setAbierto] = useState(open);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (open) {
      setMontado(true);
      // Dos `requestAnimationFrame` anidados y no uno: el primero es donde React aplica el DOM del
      // montaje; recién en el segundo el navegador ya calculó el estilo inicial y un cambio se lee
      // como transición en vez de como valor de partida.
      let raf2;
      const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setAbierto(true)); });
      return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
    }
    setAbierto(false);
    // Se desmonta por temporizador y no por `transitionend`: si se cierra mientras la pestaña está
    // en segundo plano el evento nunca llega y el nodo quedaría montado para siempre.
    timer.current = setTimeout(() => setMontado(false), duration);
    return () => clearTimeout(timer.current);
  }, [open, duration]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return {
    montado,
    estilo: {
      display: "grid",
      gridTemplateRows: abierto ? "1fr" : "0fr",
      opacity: abierto ? 1 : 0,
      transition: `grid-template-rows ${duration}ms ${REVEAL_EASE}, opacity ${duration}ms ${REVEAL_EASE}`,
    },
  };
};

// Despliegue genérico. El `className` va al contenedor INTERIOR (el que recibe el padding), no al
// que se anima: si el padding viviera afuera sería una franja de alto fijo que aparece de golpe y
// se vería el salto justo en los bordes, que es donde más se nota.
// ── ⚠️ Los children se pasan como FUNCIÓN, y no es un capricho ───────────────
// `{cond && <div/>}` hace DOS trabajos: decide qué se pinta y, sobre todo, **protege la
// evaluación** — si `cond` es falso, el JSX de adentro nunca se construye. Un componente no puede
// replicar eso: sus children son un ARGUMENTO, así que React los evalúa antes de llamarlo y
// devolver `null` llega tarde.
//
// Costó una pantalla caída para descubrirlo. `ReportsPageV2` tenía una sección cerrada por defecto
// cuyo contenido lee una configuración persistida en localStorage; con una forma vieja guardada,
// ese `map` tira `TypeError`. Llevaba ahí sin molestar a nadie porque la sección nunca se abría —
// hasta que la migración hizo que su cuerpo corriera en cada render.
//
// Por eso `children` es `() => JSX`: se invoca SÓLO cuando el nodo está montado, con lo que la
// pereza del `{cond && …}` original se conserva exacta. Se acepta igual un children normal para
// los casos donde el contenido no puede fallar, pero la función es el modo correcto por defecto.
//
// `testId` viaja al mismo div que recibe el `className` — o sea, al que ANTES era el panel. Sin
// esto, migrar una superficie le arranca su `data-testid` en silencio: compila, se ve igual, y el
// test que lo buscaba deja de encontrar nada sin que el cambio lo mencione.
export const Reveal = ({ open, duration = REVEAL_MS, className, testId, children }) => {
  const { montado, estilo } = useReveal(open, duration);
  if (!montado) return null;
  const contenido = typeof children === "function" ? children() : children;
  return (
    <div style={estilo}>
      <div className="min-h-0 overflow-hidden">
        <div className={className} data-testid={testId}>{contenido}</div>
      </div>
    </div>
  );
};

// Variante para tablas: un `<tr>` no admite el mecanismo directo —en `display: table-row` ni la
// altura ni el `overflow` se comportan— así que la animación vive en un `<div>` dentro del `<td>`.
// `cellClassName` existe porque el fondo de la fila de detalle NO es universal: Propiedades la usa
// blanca y Condominios en `bg-slate-50/40`. Fijarlo adentro habría hecho que migrar la segunda
// superficie perdiera un matiz del diseño sin que nada fallara — el modo silencioso en que un
// primitivo compartido va aplanando las pantallas que absorbe.
export const FilaExpandible = ({ open, colSpan, duration = REVEAL_MS, className, cellClassName = "bg-white", contentClassName, children }) => {
  const { montado, estilo } = useReveal(open, duration);
  if (!montado) return null;
  const contenido = typeof children === "function" ? children() : children;
  return (
    <tr className={className}>
      <td colSpan={colSpan} className={cn("p-0", cellClassName)}>
        <div style={estilo}>
          <div className="min-h-0 overflow-hidden">
            <div className={cn("px-5 py-3", contentClassName)}>{contenido}</div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// UN chevron que gira, en lugar de dos íconos que se reemplazan. El intercambio
// ChevronDown ↔ ChevronRight/Up es instantáneo por definición y contradice a un contenido que se
// despliega con transición: el ícono ya terminó de decir "abierto" mientras el panel recién empieza.
// `deg` porque las tablas rotan 180° (down → up) y los formularios 90° (right → down).
export const RevealChevron = ({ open, duration = REVEAL_MS, deg = 180, className }) => (
  <ChevronDown
    className={cn("shrink-0", className)}
    style={{
      transform: `rotate(${open ? 0 : -deg}deg)`,
      transition: `transform ${duration}ms ${REVEAL_EASE}`,
    }}
  />
);
